import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DISMISS_KEY = 'match_ready_chip_dismissed_v1';

/**
 * Floating "🎁 Match ready — load $10" chip that lives on the lobby home
 * for players who haven't claimed the new-player first-load $10 match yet.
 * Tap → routes to the automated PayPal "Buy Credits" flow.
 */
const MatchReadyChip = () => {
    const { user } = useAuth();
    const [state, setState] = useState(null);
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
    });

    const fetchState = useCallback(async () => {
        if (!user || user.role !== 'user') return;
        try {
            const { data } = await axios.get(`${API}/rewards/onboarding-status`);
            setState(data);
        } catch { /* silent */ }
    }, [user]);

    useEffect(() => { fetchState(); }, [fetchState]);
    // Re-poll every 30s so the chip disappears fast the moment the player
    // crosses the $10 threshold via cashier/receipt (no full-page reload).
    useEffect(() => {
        if (!user || user.role !== 'user') return;
        const id = setInterval(fetchState, 30_000);
        return () => clearInterval(id);
    }, [user, fetchState]);

    const goLoad = () => {
        // Route straight to the automated PayPal "Buy Credits" flow.
        window.location.assign('/buy-credits');
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
        setDismissed(true);
    };

    if (!user || user.role !== 'user') return null;
    if (!state) return null;
    if (state.is_admin || state.promotions_disabled) return null;
    if (state.first_load_match_claimed) return null;
    if (dismissed) return null;

    const min = state.first_load_match_min || 10;
    const amount = state.first_load_match_amount || 10;

    return (
        <AnimatePresence>
            <motion.div
                key="match-ready-chip"
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.9 }}
                transition={{ type: 'spring', damping: 22, stiffness: 260 }}
                className="fixed left-1/2 -translate-x-1/2 bottom-24 sm:bottom-6 z-40 pointer-events-auto"
                data-testid="match-ready-chip"
            >
                <div
                    className="relative inline-flex items-center gap-2 pl-3 pr-2 py-2 rounded-full shadow-2xl border-2"
                    style={{
                        background: 'linear-gradient(90deg, #39FF14 0%, #B8FF6B 100%)',
                        borderColor: '#052500',
                        boxShadow: '0 6px 24px rgba(57,255,20,0.45), 0 0 0 3px rgba(0,0,0,0.35)',
                    }}
                >
                    <Gift className="w-4 h-4 text-[#052500]" strokeWidth={2.8} />
                    <button
                        type="button"
                        onClick={goLoad}
                        className="text-[13px] font-black tracking-tight text-[#052500] flex items-center gap-1 hover:opacity-80 active:opacity-60 transition-opacity"
                        data-testid="match-ready-chip-cta"
                    >
                        Match ready · Load ${min} → get ${amount}
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={3} />
                    </button>
                    <button
                        type="button"
                        onClick={dismiss}
                        className="ml-1 w-5 h-5 rounded-full bg-[#052500]/25 hover:bg-[#052500]/45 text-[#052500] font-black text-[10px] flex items-center justify-center transition-colors"
                        aria-label="Dismiss"
                        data-testid="match-ready-chip-dismiss"
                    >
                        ×
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default MatchReadyChip;
